import type { ClientSubscriptionView } from '@app/components/RecordModal/components/RecordDetails/components/ClientSubscriptions/utils';
import type { Service } from '@app/components/RecordModal/components/RecordDetails/types';
import { normalizeClientSubscriptionId } from '@app/components/RecordModal/components/RecordDetails/utils/recordServices';

/** Предел количества услуги с абонемента в строке «Услуг» */
export interface SubscriptionQuantityLimit {
  /** Максимум количества: единицы в сохранённой записи + свободные */
  max: number;
  /** Свободные единицы этой услуги на абонементе (GetClientSubscriptions) */
  free: number;
}

interface CalculateSubscriptionQuantityLimitProps {
  service: Service;
  /** Строки «Услуг» записи на момент последнего сохранения или загрузки */
  savedServices: Service[];
  /** Абонементы клиента со свободными единицами */
  subscriptions: ClientSubscriptionView[];
}

/**
 * Предел количества услуги с абонемента (ТЗ us/11427, комментарий 14.10).
 * GetClientSubscriptions отдаёт только свободные единицы
 * (LogicSubscriptions.cs:583-647). Единицы, которые запись уже заняла, в
 * ответ не входят: UpdateRecord освобождает или занимает их по разнице
 * количества (LogicRecords.cs:1674-1679, :1728-1781). Поэтому максимум —
 * количество в сохранённой записи плюс свободные единицы той же услуги
 * того же абонемента. null — строка не с абонемента.
 */
export const calculateSubscriptionQuantityLimit = ({
  service,
  savedServices,
  subscriptions,
}: CalculateSubscriptionQuantityLimitProps): SubscriptionQuantityLimit | null => {
  const clientSubscriptionId = normalizeClientSubscriptionId(
    service?.clientSubscriptionId
  );

  if (clientSubscriptionId === null || !service.service) return null;

  const savedQuantity =
    savedServices.find(
      (saved) =>
        saved?.service === service.service &&
        normalizeClientSubscriptionId(saved.clientSubscriptionId) ===
          clientSubscriptionId
    )?.quantity ?? 0;
  const free =
    subscriptions
      .find(
        (subscription) =>
          subscription.clientSubscriptionId === clientSubscriptionId
      )
      ?.services.find(
        ({ clientServiceId }) => String(clientServiceId) === service.service
      )?.available ?? 0;

  return { max: savedQuantity + free, free };
};

/**
 * Ошибка количества больше предела. Предел 0 — новая строка, а свободных
 * единиц уже нет: текст называет причину и действие (UX-24). Свободных
 * единиц нет — только предел, без «осталось ещё 0». У новой строки предел — свободные единицы:
 * «На абонементе осталось N». У сохранённой строки предел больше свободных
 * на её единицы в записи, поэтому текст называет оба числа.
 */
export const getSubscriptionQuantityLimitError = ({
  max,
  free,
}: SubscriptionQuantityLimit): string => {
  if (max === 0) {
    return 'На абонементе не осталось этой услуги. Уберите её из записи';
  }

  if (free === 0) return `Не может быть больше ${max}`;

  return max === free
    ? `На абонементе осталось ${free}`
    : `Не может быть больше ${max}: на абонементе осталось ещё ${free}`;
};

/**
 * Подсказка поля количества строки с абонемента. null — подсказки нет:
 * предела нет или он 0, диапазон «от 1 до 0» не показываем (UX-24).
 */
export const getSubscriptionQuantityHint = (
  limit: SubscriptionQuantityLimit | null
): string | null =>
  limit && limit.max > 0
    ? `Услуга с абонемента: количество от 1 до ${limit.max}`
    : null;
